import EmptyStateNotice from '@/components/EmptyStateNotice';
import Input from '@/components/Input';
import ModalV2 from '@/components/ModalV2';
import { LuImageOff, LuImagePlus } from '@/components/react-icons/main';
import Spinner from '@/components/Spinner';
import sendToast from '@/lib/sendToast';
import { uploadImages } from '@/redux/actions/gravityActions';
import { setModalState } from '@/redux/actions/uiActions/modalActions';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import memoizeSelector from '@/redux/lib/memoizeSelector';
import selectors from '@/redux/selectors/selectors';
import { modal } from '@/redux/selectors/uiSelectors';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

const ImageUploaderModal = () => {
  const modalState = useSelector(memoizeSelector('modal:imageUploader', (state) => modal(state, 'imageUploader')));
  const isLoading = useArgSelector(selectors.isLoading, 'uploadImages');

  const show = modalState?.open;
  const currentImageUrl = modalState?.currentImageUrl;
  const path = modalState?.path;
  const onUpload = modalState?.onUpload;

  const [previewImage, setPreviewImage] = useState(null);
  const [isPreview, setIsPreview] = useState(false);
  const [imageError, setImageError] = useState(false);

  const validateImageFile = (file) => {
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];

    if (!validTypes.includes(file.type)) {
      sendToast({
        title: 'Invalid file type',
        message: 'Please upload a valid image file (JPEG, PNG, GIF, SVG)',
        status: 'error',
        duration: 3000,
        isClosable: true
      });

      return false;
    }

    // 5MB size limit
    if (file.size > 5 * 1024 * 1024) {
      sendToast({
        title: 'File too large',
        message: 'Image size should be less than 5MB',
        status: 'error',
        duration: 3000,
        isClosable: true
      });

      return false;
    }

    return true;
  };

  const handleFileUpload = async (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewImage(e.target.result);
      setIsPreview(true);
    };
    reader.readAsDataURL(file);

    const formData = new FormData();
    formData.append('image', file);

    const result = await uploadImages({ files: formData, path });
    const url = result?.[0];

    if (url) {
      setPreviewImage(url);

      if (typeof onUpload === 'function') {
        onUpload(result);
      }
    }

    setIsPreview(false);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (validateImageFile(file)) {
        handleFileUpload(file);
      }
    }
  };

  const handleClose = () => {
    setModalState('imageUploader', { open: false });
  };

  const actions = [
    {
      children: 'Done',
      className: 'primary',
      disabled: isLoading,
      onClick: handleClose
    }
  ];

  useEffect(() => {
    setPreviewImage(currentImageUrl);
    setIsPreview(false);
  }, [currentImageUrl]);

  useEffect(() => {
    setImageError(false);
  }, [previewImage]);

  return (
    <ModalV2 className={'image-uploader-modal'} show={show} title={'Upload Image'} footer={isLoading && <Spinner />} actions={actions} onHide={handleClose}>
      <div className={'upload-image-container'}>
        {!previewImage && <EmptyStateNotice icon={LuImagePlus} iconSize={28} description={'Click to upload or drop an image here'} />}
        <input className={'file-input'} type="file" accept="image/*" onChange={handleFileChange} />
        {previewImage && !imageError && (
          <div className="preview-container">
            <img src={previewImage} alt="Preview" className="preview-image" onError={() => setImageError(true)} />
          </div>
        )}
        {previewImage && imageError && <EmptyStateNotice icon={LuImageOff} iconSize={28} description={'Failed to load image'} />}
      </div>
      <Input disabled={isPreview} label={'Image URL'} value={previewImage} onChange={(e) => setPreviewImage(e.target.value)} />
    </ModalV2>
  );
};

export default ImageUploaderModal;
